import { apiUrl } from '../Utility/ApiUrl'
import { debounce } from '../Utility/Debounce'
import { getElements } from '../Utility/Dom'
import { reinitOnSwap } from '../Utility/SwapReinit'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_DELAY = 250
const DEFAULT_SUGGEST_TYPE_NUM = 7384

interface SuggestResponse {
  suggestions?: Record<string, number>
}

class AutocompleteService {
  private readonly containers: NodeListOf<HTMLElement>

  constructor() {
    this.containers = getElements('search-form')
  }

  public init(): void {
    this.containers.forEach((container: HTMLElement): void => {
      this.initContainer(container)
    })
  }

  private announceCount(count: number, input: HTMLInputElement): void {
    const containerId = `${input.id}-sr-announce`
    let srRegion = document.getElementById(containerId)

    if (!srRegion) {
      srRegion = document.createElement('div')
      srRegion.id = containerId
      srRegion.setAttribute('aria-live', 'polite')
      srRegion.setAttribute('aria-atomic', 'true')
      srRegion.className = 'visually-hidden'
      input.closest('.search-form__input-wrap')?.appendChild(srRegion)
    }

    srRegion.textContent = `${count} Vorschläge verfügbar`
  }

  private close(input: HTMLInputElement, listbox: HTMLUListElement): void {
    listbox.innerHTML = ''
    listbox.setAttribute('hidden', '')
    input.setAttribute('aria-expanded', 'false')
    input.removeAttribute('aria-activedescendant')

    const srRegion = document.getElementById(`${input.id}-sr-announce`)
    if (srRegion) srRegion.textContent = ''
  }

  private async fetchSuggestions(
    query: string,
    input: HTMLInputElement,
    listbox: HTMLUListElement
  ): Promise<void> {
    // Per-container `data-suggest-endpoint` swaps in a dedicated suggest URL
    // (used by the Fortbildungssuche, which has its own course-scoped endpoint).
    // Without it, fall back to the project-wide EXT:solr Suggest PageType — which
    // is a global typeNum that only resolves to JSON on the site root, not on the
    // current page (e. g. `/suche/`), so request it against the site base.
    const container = input.closest<HTMLElement>('[data-js="search-form"]')
    const endpoint = container?.dataset.suggestEndpoint
    const url = endpoint
      ? new URL(apiUrl(endpoint))
      : new URL(apiUrl('/'))
    if (endpoint) {
      url.searchParams.set('q', query)
    } else {
      url.searchParams.set('type', String(DEFAULT_SUGGEST_TYPE_NUM))
      url.searchParams.set('tx_solr[queryString]', query)
    }

    try {
      const response = await fetch(url.toString(), {
        headers: { 'Accept': 'application/json' }
      })

      if (!response.ok) return

      const data: SuggestResponse = await response.json()
      const suggestions = data.suggestions ? Object.keys(data.suggestions) : []

      this.renderSuggestions(suggestions, input, listbox)
    } catch {
      this.close(input, listbox)
    }
  }

  private handleKeyDown(
    event: KeyboardEvent,
    input: HTMLInputElement,
    listbox: HTMLUListElement
  ): void {
    const options = listbox.querySelectorAll<HTMLLIElement>('[role="option"]')
    if (options.length === 0) return

    const activeId = input.getAttribute('aria-activedescendant') ?? ''
    let activeIndex = Array.from(options).findIndex((o) => o.id === activeId)

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        activeIndex = activeIndex < options.length - 1 ? activeIndex + 1 : 0
        this.setActiveOption(input, options, activeIndex)
        break

      case 'ArrowUp':
        event.preventDefault()
        activeIndex = activeIndex > 0 ? activeIndex - 1 : options.length - 1
        this.setActiveOption(input, options, activeIndex)
        break

      case 'Enter':
        if (activeIndex >= 0 && options[activeIndex]) {
          event.preventDefault()
          input.value = options[activeIndex].dataset.value ?? options[activeIndex].textContent ?? ''
          this.close(input, listbox)
          this.submitForm(input)
        }
        break

      case 'Escape':
        event.preventDefault()
        this.close(input, listbox)
        break
    }
  }

  private highlightMatch(text: string, query: string): string {
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const idx = text.toLowerCase().indexOf(query)

    if (idx === -1 || query.length === 0) return `<span>${escaped}</span>`

    const before = escaped.substring(0, idx)
    const match = escaped.substring(idx, idx + query.length)
    const after = escaped.substring(idx + query.length)

    return `<span>${before}<mark>${match}</mark>${after}</span>`
  }

  private initContainer(container: HTMLElement): void {
    const input = container.querySelector<HTMLInputElement>('[data-js="search-form-input"]')
    const listboxId = input?.getAttribute('aria-controls')
    const listbox = listboxId ? document.getElementById(listboxId) as HTMLUListElement | null : null

    if (!input || !listbox) return

    const debouncedFetch = debounce((query: string) => this.fetchSuggestions(query, input, listbox), DEBOUNCE_DELAY)

    const resetButton = container.querySelector<HTMLButtonElement>('.search-form__reset')
    resetButton?.addEventListener('click', (event: Event): void => {
      event.preventDefault()
      input.value = ''
      input.focus()
      this.close(input, listbox)
    })

    // The clear button is a plain button (not a submit) so a blank Enter targets
    // the search-submit button, not this one. Clearing therefore re-submits the
    // form explicitly: on the result page CourseFilterLive intercepts and runs a
    // fresh (empty) search; on the hero the native GET navigation kicks in.
    const clearButton = container.querySelector<HTMLButtonElement>('[data-js="search-filter-clear"]')
    clearButton?.addEventListener('click', (event: Event): void => {
      event.preventDefault()
      input.value = ''
      this.close(input, listbox)
      input.closest('form')?.requestSubmit()
    })

    input.addEventListener('input', (): void => {
      const query = input.value.trim()

      if (query.length < MIN_QUERY_LENGTH) {
        this.close(input, listbox)
        return
      }

      debouncedFetch(query)
    })

    input.addEventListener('keydown', (event: KeyboardEvent): void => {
      this.handleKeyDown(event, input, listbox)
    })

    const outsideClick = (event: MouseEvent): void => {
      // Self-cleanup once the bound container leaves the DOM (e.g. after the
      // VHW-147 AJAX swap renders a fresh filter section).
      if (!document.contains(container)) {
        document.removeEventListener('click', outsideClick)
        return
      }
      if (!container.contains(event.target as Node)) {
        this.close(input, listbox)
      }
    }
    document.addEventListener('click', outsideClick)

    // Listbox `<li role="option">` items are not natively focusable. A
    // mousedown on one would blur the input and trigger our focusout close
    // BEFORE the click handler reads the value. Suppressing default keeps
    // focus on the input until the option's click handler runs.
    listbox.addEventListener('mousedown', (event: MouseEvent): void => {
      event.preventDefault()
    })

    container.addEventListener('focusout', (event: FocusEvent): void => {
      const next = event.relatedTarget
      if (next instanceof Node && container.contains(next)) return
      this.close(input, listbox)
    })
  }

  private renderSuggestions(
    suggestions: string[],
    input: HTMLInputElement,
    listbox: HTMLUListElement
  ): void {
    listbox.innerHTML = ''

    if (suggestions.length === 0) {
      this.close(input, listbox)
      return
    }

    const listboxId = listbox.id

    const query = input.value.trim().toLowerCase()

    suggestions.forEach((text: string, index: number): void => {
      const li = document.createElement('li')
      li.id = `${listboxId}-option-${index}`
      li.className = 'search-autocomplete__item'
      li.setAttribute('role', 'option')
      li.dataset.value = text
      li.innerHTML = this.highlightMatch(text, query)

      li.addEventListener('click', (): void => {
        input.value = text
        this.close(input, listbox)
        this.submitForm(input)
      })

      listbox.appendChild(li)
    })

    input.setAttribute('aria-expanded', 'true')
    listbox.removeAttribute('hidden')
    this.announceCount(suggestions.length, input)
  }

  private setActiveOption(
    input: HTMLInputElement,
    options: NodeListOf<HTMLLIElement>,
    index: number
  ): void {
    options.forEach((o) => {
      o.classList.remove('search-autocomplete__item--active')
      o.removeAttribute('aria-selected')
    })
    const option = options[index]
    option.classList.add('search-autocomplete__item--active')
    option.setAttribute('aria-selected', 'true')
    option.scrollIntoView({ block: 'nearest' })
    input.setAttribute('aria-activedescendant', option.id)
  }

  // Use requestSubmit when available so form-level `submit` listeners (VHW-147
  // CourseFilterLive) intercept the navigation. Plain submit() bypasses them
  // and triggers a full page reload.
  private submitForm(input: HTMLInputElement): void {
    const form = input.closest('form')
    if (!form) return
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }
}

const autocompleteService = new AutocompleteService()
autocompleteService.init()
reinitOnSwap(() => new AutocompleteService())
